import type {
  SpendLimitScope,
  TaskExecutionSummary,
  ThreadReplyView,
} from '@/lib/api/generated/model';
import { BLOCKED_SCOPE_LABEL } from '../executionStatus';

/**
 * What a person's message says about its answer (spec 149 FR-003), as one
 * line. `null` when there is nothing to say: the answer has landed and follows
 * in the thread.
 */
export interface ReplyLine {
  text: string;
  /** In motion: the line pulses gently and is announced politely. */
  live: boolean;
  /** Went wrong: said in the danger role, with Retry beside it. */
  retryable: boolean;
}

/**
 * Why a reply failed, finishing "The agent couldn't answer: …". The service's
 * run-failure categories (service spec 120) plus the two a reply adds,
 * `no_answer` and `stopped` (service spec 131).
 */
const FAILURE_CAUSE: Partial<Record<string, string>> = {
  timed_out: 'it ran out of time',
  turn_limit: 'it reached its turn limit',
  credentials_rejected: 'the provider rejected the credentials',
  provider_unavailable: 'the model provider was unavailable',
  rate_limited: 'the provider was rate-limiting',
  out_of_memory: 'it ran out of memory',
  not_started: 'it never started',
  no_answer: 'it produced no answer',
  stopped: 'it was stopped',
};

function isSpendScope(reason: string): reason is SpendLimitScope {
  return reason in BLOCKED_SCOPE_LABEL;
}

/**
 * The plan's reply-state table, row by row. A reason this client does not know
 * reads as a plain failure rather than as its code.
 */
export function replyLine(reply: ThreadReplyView): ReplyLine | null {
  switch (reply.state) {
    case 'done':
      return null;
    case 'unavailable':
      // Slice 1 of service spec 131: the thread works, nothing answers yet.
      return {
        text: 'Answers aren’t switched on yet',
        live: false,
        retryable: false,
      };
    case 'queued':
      return { text: 'Queued', live: false, retryable: false };
    case 'waiting_for_slot':
      return {
        text: 'Waiting for a free slot…',
        live: true,
        retryable: false,
      };
    case 'reading':
      return {
        text: 'Agent is reading the run…',
        live: true,
        retryable: false,
      };
    case 'answering':
      return { text: 'Agent is answering…', live: true, retryable: false };
    case 'refused': {
      // The tier that refused, never a figure: a member may see neither the
      // limit nor the spend (spec 101).
      const scope =
        reply.reason && isSpendScope(reply.reason)
          ? BLOCKED_SCOPE_LABEL[reply.reason].toLowerCase()
          : 'spend limit';
      return {
        text: `Not answered: the ${scope} was reached`,
        live: false,
        retryable: true,
      };
    }
    case 'failed': {
      const cause = reply.reason ? FAILURE_CAUSE[reply.reason] : undefined;
      return {
        text: cause
          ? `The agent couldn’t answer: ${cause}`
          : 'The agent couldn’t answer',
        live: false,
        retryable: true,
      };
    }
  }
}

/**
 * Whether a run had an agent to ask (service spec 131): an AI step's or a
 * linked workflow's, and not one that was skipped. Anything else has no
 * Conversation and no Prompt.
 */
export function isAskableRun(
  execution: Pick<TaskExecutionSummary, 'status' | 'step_kind_snapshot'>,
): boolean {
  return (
    (execution.step_kind_snapshot === 'ai' ||
      execution.step_kind_snapshot === 'linked_workflow') &&
    execution.status !== 'skipped'
  );
}

/**
 * Whether a run can be asked yet (Q6): it has finished, whichever way. `skipped`
 * is terminal too, but `isAskableRun` has already left it out.
 */
export function isFinishedRun(
  execution: Pick<TaskExecutionSummary, 'status'>,
): boolean {
  return (
    execution.status === 'completed' ||
    execution.status === 'failed' ||
    execution.status === 'stopped' ||
    execution.status === 'blocked'
  );
}
